'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _defer = require('lodash/defer');

var _defer2 = _interopRequireDefault(_defer);

var _each = require('lodash/each');

var _each2 = _interopRequireDefault(_each);

var _find = require('lodash/find');

var _find2 = _interopRequireDefault(_find);

var _isFunction = require('lodash/isFunction');

var _isFunction2 = _interopRequireDefault(_isFunction);

var _keys = require('lodash/keys');

var _keys2 = _interopRequireDefault(_keys);

var _now = require('lodash/now');

var _now2 = _interopRequireDefault(_now);

var _events = require('events');

var _events2 = _interopRequireDefault(_events);

var _debugger = require('./debugger');

var _debugger2 = _interopRequireDefault(_debugger);

var _streamUnwrap = require('./utils/streamUnwrap');

var _isAppleWebkitBrowser = require('./utils/isAppleWebkitBrowser');

var _isAppleWebkitBrowser2 = _interopRequireDefault(_isAppleWebkitBrowser);

var _StreamPlayer = require('./player/StreamPlayer');

var _StreamPlayer2 = _interopRequireDefault(_StreamPlayer);

var _deferredPromise = require('./utils/deferredPromise');

var _deferredPromise2 = _interopRequireDefault(_deferredPromise);

var _isDebugEnabled = require('./utils/isDebugEnabled');

var _isDebugEnabled2 = _interopRequireDefault(_isDebugEnabled);

var _ignoreChromeMediaEvents = require('./utils/ignoreChromeMediaEvents');

var _ignoreChromeMediaEvents2 = _interopRequireDefault(_ignoreChromeMediaEvents);

var _webApiClientSingleton = require('./webApiClientSingleton');

var _webApiClientSingleton2 = _interopRequireDefault(_webApiClientSingleton);

var _nowPlayingPoller = require('./nowPlayingPoller');

var _nowPlayingPoller2 = _interopRequireDefault(_nowPlayingPoller);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var emitter = new _events2.default();

// TODO: commenting out for now; will bring back when we start retry logic again for streams.
// const defaultRetryLimit = 0;
var defaultTimeout = 15000;

/**
 *  This is the main entry point into the application to handle all listening and
 *  reporting activities.
 *
 * @module Tuner
 */
var tuner = {
  /**
   * Enum for player events that can be fired
   *
   * @typedef PlayerEvents
   * @property {string} playing The playing event
   * @property {string} paused The stream has been paused
   * @property {string} stopped The stream has been stopped
   * @property {string} loading The stream is loading
   * @property {string} connecting Connecting to the stream
   * @property {string} waiting Waiting for stream
   * @property {string} streamFailed The stream failed to load
   * @property {string} stationFailed The station (all streams) failed to load
   * @property {string} nowPlayingChanged The stream has now changed to now playing
   * @property {string} positionChanged The position of the stream has changed
   * @property {string} mediaEnded The stream has finished
   * @property {string} qualifiedTune
   * @property {string} htmlStreamLoaded The html stream that is loaded
   * @property {string} nextTune The next stream
   * @property {string} evaluateStreamStart
   * @property {string} instreamAd The instream ad is playing
   * @property {string} meta HLS frag metadata emitted from StreamPlayer
   * @property {string} error Error emitted (for logging)
   * @property {string} playerSelected Emits the name of the selected player
   * @property {string} attemptingPlayback Attempting to load and play the stream
   */

  /**
   * @typedef params
   * @property {string} ccpaStatus
   * @property {boolean} gdprApplies
   * @property {boolean} isOptedOut
   * @property {string} ppid
   * @property {string} palNonce
   * @property {dfpParams} dfpParams
   * @property {string} audience
   */

  /**
   * @typedef dfpParams
   * @property {string} url
   * @property {string} description_url
  */

  /**
   * The tune object to load
   * @typedef {object} Tune
   * @property {string} guideId
   * @property {string} itemToken
   * @property {params} params
   * @property {string} title
   * @property {string} type
   */

  /**
   * @typedef {object} PlayerEvents
   */
  events: {
    playing: 'playing',
    paused: 'paused',
    stopped: 'stopped',
    loading: 'loading',
    connecting: 'connecting',
    waiting: 'waiting',
    streamFailed: 'streamFailed',
    stationFailed: 'stationFailed',
    nowPlayingChanged: 'nowPlayingChanged',
    positionChanged: 'positionChanged',
    mediaEnded: 'mediaEnded',
    qualifiedTune: 'qualifiedTune',
    htmlStreamLoaded: 'htmlStreamLoaded',
    nextTune: 'nextTune',
    evaluateStreamStart: 'evaluateStreamStart',
    instreamAd: 'instreamAd',
    meta: 'meta',
    error: 'error',
    playerSelected: 'playerSelected',
    attemptingPlayback: 'attemptingPlayback'
  },
  /**
   * Set up the player to play the stream
   * @function setUp
   * @param playerElement
   * @param blankMp3Url
   * @param tuneInApiClient
   * @param shoutcastExtraTimeout
   * @param timeout
   * @param isInstreamLoggingEnabled
   * @param hasNativeHlsSupport
   * @param resources
   */
  setup: function setup(_ref) {
    var _this = this;

    var playerElement = _ref.playerElement,
        blankMp3Url = _ref.blankMp3Url,
        tuneInApiClient = _ref.tuneInApiClient,
        shoutcastExtraTimeout = _ref.shoutcastExtraTimeout,
        _ref$timeout = _ref.timeout,
        timeout = _ref$timeout === undefined ? defaultTimeout : _ref$timeout,
        isInstreamLoggingEnabled = _ref.isInstreamLoggingEnabled,
        isHowlerEnabled = _ref.isHowlerEnabled,
        hasNativeHlsSupport = _ref.hasNativeHlsSupport,
        resources = _ref.resources;

    _debugger2.default.enabled = (0, _isDebugEnabled2.default)();
    _webApiClientSingleton2.default.setInstance(tuneInApiClient);
    (0, _streamUnwrap.setup)(timeout);

    (0, _ignoreChromeMediaEvents2.default)();

    this.streamPlayer = new _StreamPlayer2.default({
      playerElement: playerElement,
      blankMp3Url: blankMp3Url,
      timeout: timeout,
      isInstreamLoggingEnabled: isInstreamLoggingEnabled,
      isHowlerEnabled: isHowlerEnabled,
      tuner: this,
      hasNativeHlsSupport: hasNativeHlsSupport,
      resources: resources
    });

    // we want to generate a new listenId when the currently
    // playing episode is over. This will happen in load or play
    // functions.
    this.on(this.events.mediaEnded, function () {
      delete _this.listenId;
    });

    this.shoutcastExtraTimeout = shoutcastExtraTimeout;
    this.isAppleWebKitBrowser = (0, _isAppleWebkitBrowser2.default)();
  },

  /**
   * Load the tuner
   * @function load
   * @param {Tune} tune - The [Tune]{@link Tuner#Tune object} to set the player up with
   * @param {PlayerEvents} triggerEvent - The event that loads the player
   * @returns {Promise}
   */
  load: function load(tune, triggerEvent) {
    this.streamPlayer.clearMedia();
    this.tune = tune;
    this.streams = undefined;
    this.listenId = this.generateListenId();
    var loadingPromise = (0, _deferredPromise2.default)();

    this.state.load(loadingPromise, triggerEvent);
    return loadingPromise.promise;
  },

  /**
   * Play the stream
   * @function play
   * @event StreamPlayer#play
   */
  play: function play() {
    // in a case when media ended, since we don't make a tune call
    // we will not be calling tuner.load. So this is here to ensure
    // that we generate a new listenId. See listener in tuner.init
    if (!this.listenId) {
      this.listenId = this.generateListenId();
    }
    this.state.play();
  },
  /**
   * Pauses the stream
   * @function play
   * @event StreamPlayer#pause
   */
  pause: function pause() {
    this.state.pause();
  },
  /**
   * Stops the stream
   * @function stop
   * @event StreamPlayer#stop
   */
  stop: function stop() {
    this.state.stop();
  },
  /**
   * Scrubs the stream to a new position
   * @function seek
   * @param {number} position
   * @event StreamPlayer#seek
   */
  seek: function seek(position) {
    this.state.seek(position);
  },
  /**
   * Sets the volume on the player
   * @function setVolume
   * @param {number} volume
   */
  setVolume: function setVolume(volume) {
    this.streamPlayer.setVolume(volume);
  },
  /**
   * Requests ads to be played in stream
   * @function requestInstreamAds
   * @event StreamPlayer#requestInstreamAds
   */
  requestInstreamAds: function requestInstreamAds() {
    this.streamPlayer.requestInstreamAds();
  },
  setInstreamCustParams: function setInstreamCustParams(custParams) {
    // passed from mint on profile change
    tuner.instreamCustParams = custParams;
  },
  /**
   * Returns the current volume
   * @function getVolume
   * @returns {Number}
   */
  getVolume: function getVolume() {
    return this.streamPlayer.getVolume();
  },
  // rate needs to be a double i.e. 1.0, 2.0
  changePlaybackRate: function changePlaybackRate(rate) {
    this.streamPlayer.changePlaybackRate(rate);
  },
  canChangePlaybackRate: function canChangePlaybackRate() {
    return this.streamPlayer.canChangePlaybackRate();
  },

  /**
   * Change the state of the state machine
   * @param state
   * @param context
   */
  changeState: function changeState(state, context) {
    if (this.state !== state) {
      _debugger2.default.log(this.state.name + ' -> ' + state.name);
      if ((0, _isFunction2.default)(this.state.exit)) {
        // defer used in case there are pending promises that should be cancelled
        (0, _defer2.default)(this.state.exit);
      }

      var previousState = this.state;

      this.state = state;
      // defer used to ensure next state isn't entered before previous state is exited
      (0, _defer2.default)(this.state.enter, previousState, context);
    }
  },

  /**
   * Get the current state of the state machine
   * @function getStateName
   * @param state
   * @returns {string} Current state of machine
   */
  getStateName: function getStateName(state) {
    var states = this.states;

    var stateKeys = (0, _keys2.default)(states);
    return (0, _find2.default)(stateKeys, function (key) {
      return states[key] === state;
    });
  },

  /**
   * Called from gemini-web to dispatch actions in response to tuner events
   * @function on
   * @listens module:Tuner~PlayerEvents
   * @param event The player event being emitted
   * @param {function} eventHandler The callback event handler
   */
  on: emitter.on.bind(emitter),

  /**
   * Remove the event handler
   * @function off
   * @param {EventSource} event
   * @param {function} eventHandler The callback event handler
   */
  off: function off(event, eventHandler) {
    // called from gemini-web to remove event handlers from tuner events
    if (event) {
      emitter.off(event, eventHandler);
    } else {
      (0, _each2.default)(this.events, function (_event) {
        emitter.removeAllListeners(_event);
      });
    }
  },

  /**
   * Fires the event
   * @function trigger
   * @param {PlayerEvents} event One of the Player events
   * @param {object} data The data to emit
   */
  trigger: emitter.emit.bind(emitter),

  /**
   * Generate a unique listenID
   * @function generate
   * @returns {number}
   */
  generateListenId: function generateListenId() {
    // exported for testing
    return Math.round((0, _now2.default)() / 1000);
  },

  stopPoller: function stopPoller() {
    _nowPlayingPoller2.default.stop();
  },

  preTune: function preTune() {
    this.streamPlayer.preload();
  }
};

exports.default = tuner;
//# sourceMappingURL=tuner.js.map